import { useQuery } from '@tanstack/react-query';
import { diaryApi } from '../api/endpoints.js';
import { formatNumber } from '../lib/format.js';
import { FlameIcon } from './icons.js';

/**
 * How consistently the diary is kept: the current streak, and what share of the
 * window has any entry at all.
 *
 * The coverage figure is not decoration. Every average the app reports — the
 * Verlauf screen's kcal and macros — is taken over *logged* days, so a headline
 * average means something quite different at 9 of 30 days than at 29. Showing
 * both keeps the reader honest about which they are looking at.
 */
export function LoggingStats({ days = 90 }: { days?: number }): JSX.Element | null {
  const stats = useQuery({
    queryKey: ['diary', 'stats', days],
    queryFn: () => diaryApi.stats(days),
  });

  if (!stats.data) return null;

  const { currentStreak, longestStreak, loggedDays, windowDays, loggedToday } = stats.data;
  if (loggedDays === 0) return null;

  const coverage = windowDays > 0 ? (loggedDays / windowDays) * 100 : 0;

  return (
    <div className="card stack">
      <div className="row-between">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FlameIcon />
          <span>Dranbleiben</span>
        </div>
        {currentStreak > 0 && (
          <span className={loggedToday ? 'badge badge-accent' : 'badge'}>
            {currentStreak} {currentStreak === 1 ? 'Tag' : 'Tage'} in Folge
          </span>
        )}
      </div>

      {currentStreak === 0 ? (
        <span className="hint">
          Die Serie ist gerissen — ein Eintrag heute startet sie neu.
          {longestStreak > 1 ? ` Dein Rekord steht bei ${longestStreak} Tagen.` : ''}
        </span>
      ) : (
        <span className="hint">
          {loggedToday
            ? 'Heute ist erfasst.'
            : 'Heute fehlt noch — die Serie läuft weiter, solange du den Tag nicht auslässt.'}
          {longestStreak > currentStreak ? ` Rekord: ${longestStreak} Tage.` : ''}
        </span>
      )}

      <div>
        <div className="row-between">
          <span className="hint">
            {loggedDays} von {windowDays} Tagen erfasst
          </span>
          <span className="hint">{formatNumber(coverage)} %</span>
        </div>
        <div className="macro-bar" style={{ height: 6 }}>
          <span style={{ width: `${coverage}%`, background: 'var(--accent)' }} />
        </div>
      </div>

      {coverage < 60 && (
        <span className="hint">
          Durchschnitte im Verlauf zählen nur erfasste Tage — bei dieser Quote sind sie eher ein
          Anhaltspunkt als eine Bilanz.
        </span>
      )}
    </div>
  );
}
